import React, { useState, useEffect } from 'react';
import {
  Typography,
  Card,
  CardContent,
  Box,
  Grid,
  Chip,
  CircularProgress,
  Button,
  Divider,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';

const PortalPurchaseDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [purchase, setPurchase] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPurchaseDetail();
  }, [id]);

  const fetchPurchaseDetail = async () => {
    try {
      const token = localStorage.getItem('portal_access_token');
      const response = await api.get(`/portal/purchases/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setPurchase(response.data.purchase);
    } catch (error) {
      console.error('Failed to fetch purchase detail:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!purchase) {
    return <Typography>Purchase not found</Typography>;
  }

  return (
    <Box>
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/portal/purchases')} sx={{ mb: 2 }}>
        Back to Purchases
      </Button>

      <Typography variant="h4" gutterBottom>
        Purchase Details
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h5" gutterBottom>
                {purchase.item?.name}
              </Typography>

              {purchase.item?.photos?.length > 0 && (
                <Grid container spacing={1} sx={{ mt: 2 }}>
                  {purchase.item.photos.map((photo, index) => (
                    <Grid item xs={6} sm={4} key={index}>
                      <Box
                        component="img"
                        src={`${process.env.REACT_APP_API_URL || 'https://api.paceestate.com'}${photo.file_path}`}
                        alt={`${purchase.item.name} ${index + 1}`}
                        sx={{ width: '100%', height: 200, objectFit: 'cover', borderRadius: 1 }}
                      />
                    </Grid>
                  ))}
                </Grid>
              )}

              <Typography variant="body1" sx={{ mt: 3 }}>
                {purchase.item?.description}
              </Typography>

              {purchase.notes && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Notes:
                  </Typography>
                  <Typography variant="body2">{purchase.notes}</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Purchase Information
              </Typography>

              <Box sx={{ my: 2 }}>
                <Typography variant="body2" color="text.secondary">Price</Typography>
                <Typography variant="h6">${purchase.price_paid}</Typography>
              </Box>

              <Box sx={{ my: 2 }}>
                <Typography variant="body2" color="text.secondary">Tax</Typography>
                <Typography>${purchase.tax_amount || 0}</Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ my: 2 }}>
                <Typography variant="body2" color="text.secondary">Total</Typography>
                <Typography variant="h5" color="primary">${purchase.total_amount}</Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ my: 2 }}>
                <Typography variant="body2" color="text.secondary">Payment Method</Typography>
                <Typography>{purchase.payment_method}</Typography>
              </Box>

              <Box sx={{ my: 2 }}>
                <Typography variant="body2" color="text.secondary">Purchase Date</Typography>
                <Typography>{new Date(purchase.purchased_at).toLocaleDateString()}</Typography>
              </Box>

              <Box sx={{ my: 2 }}>
                <Typography variant="body2" color="text.secondary">Pickup Status</Typography>
                <Chip label={purchase.pickup_status} color="primary" size="small" />
              </Box>

              {purchase.pickup_date && (
                <Box sx={{ my: 2 }}>
                  <Typography variant="body2" color="text.secondary">Pickup Date</Typography>
                  <Typography>{new Date(purchase.pickup_date).toLocaleDateString()}</Typography>
                </Box>
              )}
            </CardContent>
          </Card>

          {purchase.sale && (
            <Card sx={{ mt: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Sale Information
                </Typography>
                <Typography variant="body1">{purchase.sale.name}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {purchase.sale.address}<br />
                  {purchase.sale.city}, {purchase.sale.state}
                </Typography>
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default PortalPurchaseDetail;
